import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { creatorApi, activitiesApi, couponsApi, uploadApi, registrationsApi, messagingApi, usersApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { CATEGORIES, formatDate } from '../lib/utils';
import { getStates, getCities } from '../lib/geoData';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Plus, 
  Calendar as CalendarIcon, 
  Users, 
  Star, 
  TrendingUp,
  Edit,
  Trash2,
  Copy,
  Tag,
  Image as ImageIcon,
  X,
  CheckCircle,
  Clock,
  DollarSign,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  CalendarPlus,
  Eye
} from 'lucide-react';

// Mexican bank codes from CLABE prefix (first 3 digits)
const CLABE_BANKS = {'002':'ABM','006':'Bancomext','009':'Banobras','012':'BBVA','014':'Santander','021':'HSBC','030':'Bajío','032':'IXE','036':'Inbursa','037':'Interacciones','042':'Mifel','044':'Scotiabank','058':'Banregio','059':'Invex','060':'Bansi','062':'Afirme','072':'Banorte','102':'ABN AMRO','103':'American Express','106':'BAMSA','108':'Tokyo','110':'JP Morgan','112':'Bansí','113':'CI Banco','116':'ING','124':'Deutsche','126':'Credit Suisse','127':'Azteca','128':'Autofin','129':'Barclays','130':'Compartamos','131':'Banco Famsa','132':'Multiva','133':'Actinver','134':'Intercam','136':'HSBC','137':'Bancoppel','138':'ABC Capital','139':'UBS','140':'Consubanco','141':'Volkswagen','143':'CIBanco','145':'Bbase','147':'Bankaool','148':'Pagatodo','150':'Inmobiliario','155':'ICBC','156':'Sabadell','157':'Shinhan','166':'BanCoppel'};
const getBankFromClabe = (clabe) => clabe?.length >= 3 ? CLABE_BANKS[clabe.slice(0, 3)] || null : null;

// Generate time slots every 30 min from 00:00 to 23:30
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const ampm = h < 12 ? 'AM' : 'PM';
      const label = `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
      slots.push({ value: val, label });
    }
  }
  return slots;
})();

const TimeSelect = ({ value, onChange, className = '' }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`${className} text-xs`} data-testid="time-select">
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="max-h-56">
      {TIME_SLOTS.map(s => (
        <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const CreatorDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const { t, language } = useLanguage();

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState('general');
  const [showCreateActivity, setShowCreateActivity] = useState(false);
  const [showCreateCoupon, setShowCreateCoupon] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showRegistrations, setShowRegistrations] = useState(false);
  const [registrations, setRegistrations] = useState({ confirmed: [], pending_payment: [], waitlist: [], total_confirmed: 0, total_pending: 0, total_waitlist: 0 });
  const [selectedRegActivity, setSelectedRegActivity] = useState(null);
  const [regActivities, setRegActivities] = useState([]);
  const [viewingActivity, setViewingActivity] = useState(null);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState({
    payment_link: '',
    bank_info: {
      bank_name: '',
      account_holder: '',
      account_number: '',
      clabe: ''
    }
  });
  const [savingPaymentMethods, setSavingPaymentMethods] = useState(false);
  const [paymentRules, setPaymentRules] = useState({
    range_48h_24h: true,
    range_24h_6h: true,
    range_6h_3h: true,
    cutoff_hours: 3
  });
  const [savingRules, setSavingRules] = useState(false);
  const [cancellationRules, setCancellationRules] = useState({
    min_cancel_hours: 24,
    rules: [
      { min_hours: 48, refund_percent: 100 },
      { min_hours: 24, refund_percent: 50 },
    ]
  });
  const [analytics, setAnalytics] = useState(null);
  const [analyticsFilter, setAnalyticsFilter] = useState({ year: '', month: '', activity_id: '', status: '' });
  const [generalStats, setGeneralStats] = useState(null);
  const [statsFilter, setStatsFilter] = useState({ year: '', month: '', activity_id: '', status: '' });

  // Activity form state
  const [activityForm, setActivityForm] = useState({
    title: '',
    short_description: '',
    long_description: '',
    categories: ['bienestar'],
    cover_image: '',
    gallery: [],
    dates: [{ date: '', time: '10:00', end_time: '12:00', capacity: '' }],
    capacity: '',
    address: '',
    city: '',
    state: '',
    neighborhood: '',
    min_age: null,
    price: '',
    cancellation_policy: '',
    faqs: [],
    allow_waitlist: true,
    tags: [],
    coupon: { code: '', discount_amount: '', max_uses: '', expires_at: '' }
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);

  // Coupon form state
  const [couponForm, setCouponForm] = useState({
    code: '',
    discount_percent: 10,
    activity_id: '',
    max_uses: 100
  });

  useEffect(() => {
    // Wait for user data to be loaded
    if (!user) {
      return;
    }
    if (user.role !== 'creator') {
      navigate('/prelanzamiento');
      return;
    }
    fetchDashboard();
    fetchCoupons();
    fetchPaymentMethods();
    fetchAnalytics();
    fetchGeneralStats();
  }, [user, navigate]);

  const fetchDashboard = async () => {
    try {
      const response = await creatorApi.getDashboard();
      setDashboard(response.data);
      // Also set regActivities from dashboard activities (combine active and past)
      const allActivities = [
        ...(response.data?.active_activities || []),
        ...(response.data?.past_activities || [])
      ];
      setRegActivities(allActivities);
      
      // Auto-open activity from URL query params (e.g. from notification click)
      const params = new URLSearchParams(location.search);
      const activityParam = params.get('activity');
      if (activityParam && !viewingActivity) {
        const match = allActivities.find(a => a.activity_id === activityParam);
        if (match) {
          setMainTab('active');
          handleOpenActivity(match);
          // Clean URL params
          navigate('/dashboard', { replace: true });
        }
      }
    } catch (error) {
      toast.error(t('msg.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityRegistrations = async (activityId) => {
    try {
      setSelectedRegActivity(activityId);
      const response = await activitiesApi.getRegistrations(activityId);
      setRegistrations(response.data);
    } catch (error) { console.warn('fetchRegistrations:', error.message); }
  };

  const fetchGeneralStats = async (filters = statsFilter) => {
    try {
      const params = {};
      if (filters.year) params.year = filters.year;
      if (filters.month) params.month = filters.month;
      if (filters.activity_id) params.activity_id = filters.activity_id;
      if (filters.status) params.status = filters.status;
      const res = await creatorApi.getGeneralStats(params);
      setGeneralStats(res.data);
    } catch (error) { console.warn('non-critical:', error.message); }
  };

  const handleStatsFilterChange = (key, value) => {
    const newFilter = { ...statsFilter, [key]: value };
    if (key === 'year' && !value) newFilter.month = '';
    setStatsFilter(newFilter);
    fetchGeneralStats(newFilter);
  };

  const handleConfirmPaymentFromTab = async (registrationId) => {
    try {
      await registrationsApi.confirmPayment(registrationId);
      toast.success('Pago confirmado');
      fetchActivityRegistrations(selectedRegActivity);
      fetchDashboard();
    } catch (error) {
      toast.error('Error al confirmar pago');
    }
  };

  const fetchCoupons = async () => {
    try {
      const response = await couponsApi.getMine();
      setCoupons(response.data);
    } catch (error) { console.warn('non-critical:', error.message); }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await creatorApi.getPaymentMethods();
      if (response.data) {
        setPaymentMethods(response.data);
        if (response.data.payment_rules) {
          setPaymentRules(response.data.payment_rules);
        }
        if (response.data.cancellation_rules) {
          setCancellationRules(response.data.cancellation_rules);
        }
      }
    } catch (error) { console.warn('non-critical:', error.message); }
  };

  const handleSavePaymentMethods = async () => {
    setSavingPaymentMethods(true);
    try {
      await creatorApi.updatePaymentMethods(paymentMethods);
      toast.success('Métodos de pago guardados');
      setShowPaymentMethods(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingPaymentMethods(false);
    }
  };

  const handleSavePaymentRules = async () => {
    setSavingRules(true);
    try {
      await creatorApi.updatePaymentMethods({ ...paymentMethods, payment_rules: paymentRules, cancellation_rules: cancellationRules });
      toast.success('Reglas de pago guardadas');
    } catch (error) {
      toast.error('Error al guardar reglas');
    } finally {
      setSavingRules(false);
    }
  };


  const fetchAnalytics = async (filters = analyticsFilter) => {
    try {
      const params = {};
      if (filters.year) params.year = filters.year;
      if (filters.month) params.month = filters.month;
      if (filters.activity_id) params.activity_id = filters.activity_id;
      if (filters.status) params.status = filters.status;
      const res = await creatorApi.getAnalytics(params);
      setAnalytics(res.data);
    } catch (error) { console.warn('non-critical:', error.message); }
  };

  const handleAnalyticsFilterChange = (key, value) => {
    const newFilter = { ...analyticsFilter, [key]: value };
    if (key === 'year' && !value) newFilter.month = '';
    setAnalyticsFilter(newFilter);
    fetchAnalytics(newFilter);
  };

  const handleImageUpload = async (e, isGallery = false) => {
    const files = e.target.files;
    if (!files) return;

    setUploading(true);
    try {
      for (const file of files) {
        const result = await uploadApi.uploadFile(file);
        const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
        
        if (isGallery) {
          setActivityForm(prev => ({
            ...prev,
            gallery: [...prev.gallery, imageUrl].slice(0, 10)
          }));
        } else {
          setActivityForm(prev => ({
            ...prev,
            cover_image: imageUrl
          }));
        }
      }
      toast.success('Imagen subida');
    } catch (error) {
      toast.error('Error al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImage = (index) => {
    setActivityForm(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
  };

  const handleDateSelect = (date) => {
    if (date) {
      const formattedDate = format(date, 'yyyy-MM-dd');
      // Check if date is within 2 months
      const maxDate = new Date();
      maxDate.setMonth(maxDate.getMonth() + 2);
      if (date > maxDate) {
        toast.error('Las actividades no pueden ser más de 2 meses en el futuro');
        return;
      }
      
      const currentDates = [...activityForm.dates];
      if (currentDates[0].date === '') {
        currentDates[0].date = formattedDate;
      } else {
        currentDates.push({ date: formattedDate, time: '10:00', end_time: '12:00', capacity: '' });
      }
      setActivityForm(prev => ({ ...prev, dates: currentDates }));
    }
    setSelectedDate(date);
  };

  const removeDate = (index) => {
    setActivityForm(prev => {
      const remaining = prev.dates.filter((_, i) => i !== index);
      return {
        ...prev,
        dates: remaining.length > 0 ? remaining : [{ date: '', time: '10:00', end_time: '12:00', capacity: '' }]
      };
    });
  };

  const updateDateTime = (index, field, value) => {
    setActivityForm(prev => ({
      ...prev,
      dates: prev.dates.map((d, i) => i === index ? { ...d, [field]: value } : d)
    }));
  };

  const handleSaveActivity = async () => {
    if (!activityForm.title || !activityForm.dates[0]?.date) {
      toast.error('Completa los campos requeridos *');
      return;
    }

    if (!activityForm.cover_image) {
      toast.error('La imagen de portada es requerida *');
      return;
    }

    if (!activityForm.categories?.length) {
      toast.error('Selecciona al menos una categoría *');
      return;
    }

    if (!activityForm.short_description?.trim()) {
      toast.error('La descripción corta es requerida *');
      return;
    }

    if (!activityForm.long_description?.trim()) {
      toast.error('La descripción larga es requerida *');
      return;
    }

    if (activityForm.gallery.length < 4) {
      toast.error('Se requieren mínimo 4 imágenes en la galería *');
      return;
    }

    // Validate at least one date has capacity
    const hasCapacity = activityForm.dates.some(d => d.date && d.capacity);
    if (!hasCapacity && !activityForm.capacity) {
      toast.error('Define el cupo en al menos una fecha *');
      return;
    }

    if (!activityForm.min_age && activityForm.min_age !== 0) {
      toast.error('La edad mínima es requerida *');
      return;
    }

    if (!activityForm.state || !activityForm.city || activityForm.city === '__other__' || !activityForm.address?.trim()) {
      toast.error('Completa la dirección (estado, ciudad y calle) *');
      return;
    }

    if (activityForm.price === '' || activityForm.price === null || activityForm.price === undefined) {
      toast.error('El precio es requerido. Escribe 0 para actividades gratuitas *');
      return;
    }

    const price = parseFloat(activityForm.price) || 0;

    // Validate cancellation policy for paid activities
    if (price > 0 && cancellationRules.rules?.length === 0) {
      toast.error('Configura las reglas de cancelación en la sección de Pagos *');
      return;
    }

    // Auto-save any unsaved FAQ in the input fields
    let finalFaqs = [...activityForm.faqs];
    const pendingQ = document.getElementById('faq-question')?.value?.trim();
    const pendingA = document.getElementById('faq-answer')?.value?.trim();
    if (pendingQ && pendingA) {
      finalFaqs.push({ question: pendingQ, answer: pendingA });
      document.getElementById('faq-question').value = '';
      document.getElementById('faq-answer').value = '';
    }

    if (finalFaqs.length < 1) {
      toast.error('Agrega al menos 1 pregunta frecuente *');
      return;
    }

    // Build form data - categories sent as array, backend uses first as primary
    // Derive capacity from max per-date capacity
    const dateCapacities = activityForm.dates
      .filter(d => d.date && d.capacity)
      .map(d => parseInt(d.capacity));
    const derivedCapacity = dateCapacities.length > 0 ? Math.max(...dateCapacities) : (activityForm.capacity || 0);

    const formData = { 
      ...activityForm,
      faqs: finalFaqs,
      price,
      capacity: derivedCapacity,
      category: activityForm.categories[0],
      categories: activityForm.categories,
    };

    setSaving(true);
    try {
      if (editingActivity) {
        await activitiesApi.update(editingActivity.activity_id, formData);
        toast.success('Actividad actualizada');
      } else {
        await activitiesApi.create(formData);
        toast.success('Actividad creada');
      }
      setShowCreateActivity(false);
      resetActivityForm();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('msg.error'));
    } finally {
      setSaving(false);
    }
  };

  const resetActivityForm = () => {
    setActivityForm({
      title: '',
      short_description: '',
      long_description: '',
      categories: ['bienestar'],
      cover_image: '',
      gallery: [],
      dates: [{ date: '', time: '10:00', end_time: '12:00', capacity: '' }],
      capacity: '',
      address: '',
      city: '',
      min_age: null,
      price: '',
      cancellation_policy: '',
      faqs: [],
      allow_waitlist: true,
      tags: [],
      coupon: { code: '', discount_amount: '', max_uses: '' }
    });
    setEditingActivity(null);
  };

  const handleEditActivity = (activity) => {
    setActivityForm({
      title: activity.title,
      short_description: activity.short_description,
      long_description: activity.long_description,
      categories: activity.categories || (activity.category ? [activity.category] : ['bienestar']),
      cover_image: activity.cover_image || '',
      gallery: activity.gallery || [],
      dates: activity.dates,
      capacity: activity.capacity,
      address: activity.address,
      city: activity.city,
      state: activity.state || '',
      neighborhood: activity.neighborhood || '',
      min_age: activity.min_age,
      price: activity.price || '',
      cancellation_policy: activity.cancellation_policy || '',
      faqs: activity.faqs || [],
      allow_waitlist: activity.allow_waitlist,
      tags: activity.tags || []
    });
    setEditingActivity(activity);
    setShowCreateActivity(true);
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta actividad?')) return;
    try {
      await activitiesApi.delete(activityId);
      toast.success('Actividad eliminada');
      fetchDashboard();
    } catch (error) {
      toast.error(t('msg.error'));
    }
  };

  const handleReuseActivity = async (activity) => {
    setActivityForm({
      title: activity.title,
      short_description: activity.short_description,
      long_description: activity.long_description,
      categories: activity.categories || (activity.category ? [activity.category] : ['bienestar']),
      cover_image: activity.cover_image || '',
      gallery: activity.gallery || [],
      dates: [{ date: '', time: '10:00', end_time: '12:00', capacity: '' }],
      capacity: activity.capacity,
      address: activity.address,
      city: activity.city,
      state: activity.state || '',
      neighborhood: activity.neighborhood || '',
      min_age: activity.min_age,
      price: activity.price || '',
      cancellation_policy: activity.cancellation_policy || '',
      faqs: activity.faqs || [],
      allow_waitlist: activity.allow_waitlist,
      tags: activity.tags || []
    });
    setEditingActivity(null);
    setShowCreateActivity(true);
  };

  const handleViewRegistrations = async (activity) => {
    setSelectedActivity(activity);
    try {
      const response = await activitiesApi.getRegistrations(activity.activity_id);
      setRegistrations(response.data);
      setShowRegistrations(true);
    } catch (error) {
      toast.error(t('msg.error'));
    }
  };

  const handleOpenActivity = async (activity) => {
    setViewingActivity(activity);
    try {
      const response = await activitiesApi.getRegistrations(activity.activity_id);
      setRegistrations(response.data);
    } catch (error) { console.warn('non-critical:', error.message); }
  };

  const handleConfirmPayment = async (registrationId) => {
    try {
      await registrationsApi.confirmPayment(registrationId);
      toast.success('Pago confirmado');
      // Move from pending to confirmed locally
      setRegistrations(prev => {
        const confirmed_reg = prev.pending_payment.find(r => r.registration_id === registrationId);
        if (!confirmed_reg) return prev;
        return {
          ...prev,
          pending_payment: prev.pending_payment.filter(r => r.registration_id !== registrationId),
          confirmed: [...prev.confirmed, { ...confirmed_reg, status: 'confirmed' }],
          total_pending: prev.total_pending - 1,
          total_confirmed: prev.total_confirmed + 1
        };
      });
      // Refresh dashboard counts
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al confirmar pago');
    }
  };

  const handlePaymentNotFound = async (registrationId) => {
    try {
      await registrationsApi.paymentNotFound(registrationId);
      toast.success('Usuario notificado - tiene 24h para responder');
      setRegistrations(prev => ({
        ...prev,
        pending_payment: prev.pending_payment.map(r => 
          r.registration_id === registrationId 
            ? { ...r, status: 'payment_not_found' }
            : r
        )
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleCreateCoupon = async () => {
    if (!couponForm.code || !couponForm.activity_id) {
      toast.error('Completa los campos requeridos');
      return;
    }
    try {
      await couponsApi.create(couponForm);
      toast.success('Cupón creado');
      setShowCreateCoupon(false);
      setCouponForm({ code: '', discount_percent: 10, activity_id: '', max_uses: 100 });
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('msg.error'));
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    try {
      await couponsApi.delete(couponId);
      toast.success('Cupón eliminado');
      fetchCoupons();
    } catch (error) {
      toast.error(t('msg.error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="skeleton w-16 h-16 rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto">
        {/* Sticky Header */}
        <div className="sticky top-14 md:top-11 z-30 bg-[#FAFAFA] px-4 pt-4 pb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
            <h1 className="font-cabinet text-3xl font-black text-slate-900">
              {t('dashboard.title')}
            </h1>
            <p className="text-slate-500">Hola, {user?.name}</p>
          </div>
          <Button 
            onClick={() => { resetActivityForm(); setShowCreateActivity(true); }}
            className="bg-[#7f9df1] hover:bg-[#6b8be0] rounded-full"
            data-testid="create-activity-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('dashboard.createActivity')}
          </Button>
          </div>
        </div>

        <div className="px-4">

        {/* Tabs */}
        {viewingActivity ? (
          /* ====== ACTIVITY DETAIL VIEW (Tasks 5 & 6) ====== */
          <ActivityDetailView
            activity={viewingActivity}
            registrations={registrations}
            onBack={() => { setViewingActivity(null); fetchDashboard(); }}
            onEdit={() => handleEditActivity(viewingActivity)}
            onDelete={() => { handleDeleteActivity(viewingActivity.activity_id); setViewingActivity(null); }}
            onConfirmPayment={handleConfirmPaymentFromTab}
            onPaymentNotFound={handlePaymentNotFound}
            onActivityCancelled={(updatedActivity) => setViewingActivity(updatedActivity)}
            language={language}
            token={token}
          />
        ) : (
        <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
          <TabsList className="mb-6 grid grid-cols-4 bg-gray-100 p-1.5 rounded-xl h-auto">
            <TabsTrigger value="general" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-[10px] md:text-xs py-2.5 px-2 whitespace-nowrap" data-testid="tab-general">General</TabsTrigger>
            <TabsTrigger value="active" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-[10px] md:text-xs py-2.5 px-2 whitespace-nowrap" data-testid="tab-activities">Actividades</TabsTrigger>
            <TabsTrigger value="payment-methods" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-[10px] md:text-xs py-2.5 px-2 whitespace-nowrap" data-testid="tab-payment-methods">Pagos</TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-[10px] md:text-xs py-2.5 px-2 whitespace-nowrap" data-testid="tab-analytics">Analytics</TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general">
            <div className="space-y-6">
              {/* Filters */}
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={statsFilter.status}
                  onChange={(e) => handleStatsFilterChange('status', e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-2 bg-white w-full"
                  data-testid="filter-status"
                >
                  <option value="">Todas</option>
                  <option value="active">Solo activas</option>
                  <option value="past">Solo pasadas</option>
                </select>
                <select
                  value={statsFilter.year}
                  onChange={(e) => handleStatsFilterChange('year', e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-2 bg-white w-full"
                  data-testid="filter-year"
                >
                  <option value="">Todos los años</option>
                  {[2026, 2025, 2024].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select
                  value={statsFilter.month}
                  onChange={(e) => handleStatsFilterChange('month', e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-2 bg-white w-full"
                  disabled={!statsFilter.year}
                  data-testid="filter-month"
                >
                  <option value="">Todos los meses</option>
                  {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((m, i) => (
                    <option key={i+1} value={String(i+1)}>{m}</option>
                  ))}
                </select>
                <select
                  value={statsFilter.activity_id}
                  onChange={(e) => handleStatsFilterChange('activity_id', e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-2 bg-white w-full truncate"
                  data-testid="filter-activity"
                >
                  <option value="">Todas las actividades</option>
                  {(generalStats?.all_activities || []).filter(a => a.is_active).map(a => (
                    <option key={a.activity_id} value={a.activity_id}>{a.title}</option>
                  ))}
                  {(generalStats?.all_activities || []).some(a => !a.is_active) && (
                    <option disabled>── Pasadas ──</option>
                  )}
                  {(generalStats?.all_activities || []).filter(a => !a.is_active).map(a => (
                    <option key={a.activity_id} value={a.activity_id}>{a.title}</option>
                  ))}
                </select>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white rounded-xl p-4 border border-slate-900/10">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="p-1.5 rounded-lg bg-[#7f9df1]/10">
                      <CalendarIcon className="w-4 h-4 text-[#7f9df1]" />
                    </div>
                    <span className="text-slate-500 text-xs">Activas</span>
                  </div>
                  <p className="font-cabinet text-2xl font-bold text-slate-900">
                    {generalStats?.active_count ?? dashboard?.active_activities?.length ?? 0}
                  </p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-900/10">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="p-1.5 rounded-lg bg-purple-100">
                      <Users className="w-4 h-4 text-purple-600" />
                    </div>
                    <span className="text-slate-500 text-xs">Registros</span>
                  </div>
                  <p className="font-cabinet text-2xl font-bold text-slate-900">
                    {generalStats?.total_registrations ?? dashboard?.total_registrations ?? 0}
                  </p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-900/10">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="p-1.5 rounded-lg bg-amber-100">
                      <Star className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="text-slate-500 text-xs">Calificación</span>
                  </div>
                  <p className="font-cabinet text-2xl font-bold text-slate-900">
                    {generalStats?.avg_rating || dashboard?.avg_rating || '-'}
                  </p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-900/10">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="p-1.5 rounded-lg bg-green-100">
                      <DollarSign className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-slate-500 text-xs">Ingresos</span>
                  </div>
                  <p className="font-cabinet text-2xl font-bold text-slate-900">
                    ${(generalStats?.revenue?.total_confirmed ?? dashboard?.revenue?.total_confirmed ?? 0).toLocaleString('es-MX')} MXN
                  </p>
                </div>
              </div>

              {/* Revenue Summary */}
              <div className="bg-white rounded-xl p-6 border border-slate-900/10">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#7f9df1]" />
                  Resumen de pagos
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-xs text-green-600 font-medium mb-1">Pagos confirmados</p>
                    <p className="text-lg font-bold text-green-700">${(generalStats?.revenue?.total_confirmed ?? dashboard?.revenue?.total_confirmed ?? 0).toLocaleString('es-MX')}</p>
                    <p className="text-xs text-green-500 mt-1">{generalStats?.revenue?.confirmed_count ?? dashboard?.revenue?.confirmed_count ?? 0} reservas</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-600 font-medium mb-1">Pagos pendientes</p>
                    <p className="text-lg font-bold text-amber-700">${(generalStats?.revenue?.total_pending ?? dashboard?.revenue?.total_pending ?? 0).toLocaleString('es-MX')}</p>
                    <p className="text-xs text-amber-500 mt-1">{generalStats?.revenue?.pending_count ?? dashboard?.revenue?.pending_count ?? 0} reservas</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600 font-medium mb-1">Total esperado</p>
                    <p className="text-lg font-bold text-blue-700">${(
                      (generalStats?.revenue?.total_confirmed ?? dashboard?.revenue?.total_confirmed ?? 0) +
                      (generalStats?.revenue?.total_pending ?? dashboard?.revenue?.total_pending ?? 0)
                    ).toLocaleString('es-MX')}</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Mis Actividades Tab - Active + Past combined */}
          <TabsContent value="active">
            {/* Active activities */}
            {dashboard?.active_activities?.length === 0 && dashboard?.past_activities?.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-900/10">
                <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No tienes actividades</p>
                <Button 
                  onClick={() => setShowCreateActivity(true)}
                  className="bg-[#7f9df1] hover:bg-[#6b8be0]"
                >
                  Crear tu primera actividad
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Active */}
                {dashboard?.active_activities?.length > 0 && (
                  <CollapsibleSection title="Activas" count={dashboard.active_activities.length}>
                    {dashboard.active_activities.map((activity) => (
                      <ActivityRow 
                        key={activity.activity_id}
                        activity={activity}
                        onOpen={() => handleOpenActivity(activity)}
                        language={language}
                      />
                    ))}
                  </CollapsibleSection>
                )}
                {/* Refund section */}
                {dashboard?.refund_activities?.length > 0 && (
                  <CollapsibleSection title="Reembolso" count={dashboard.refund_activities.length}>
                    {dashboard.refund_activities.map((activity) => (
                      <div
                        key={activity.activity_id}
                        className="w-full bg-amber-50 rounded-xl p-4 border border-amber-200 cursor-pointer hover:shadow-sm transition-shadow"
                        onClick={() => handleOpenActivity(activity)}
                        data-testid={`refund-row-${activity.activity_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                            <img src={activity.cover_image || 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=200'} alt={activity.title} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-slate-900 text-sm truncate">{activity.title}</h3>
                            <p className="text-xs text-amber-600">{activity.refund_pending_count} reembolso(s) pendiente(s) de {activity.refund_total_count}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-amber-400 flex-shrink-0" />
                        </div>
                      </div>
                    ))}
                  </CollapsibleSection>
                )}
                {/* Past */}
                {dashboard?.past_activities?.length > 0 && (
                  <CollapsibleSection title="Pasadas" count={dashboard.past_activities.length} defaultOpen={dashboard.active_activities.length === 0 && (dashboard.refund_activities?.length || 0) === 0}>
                    {dashboard.past_activities.map((activity) => (
                      <ActivityRow 
                        key={activity.activity_id}
                        activity={activity}
                        isPast
                        onOpen={() => handleOpenActivity(activity)}
                        onReuse={() => handleReuseActivity(activity)}
                        language={language}
                      />
                    ))}
                  </CollapsibleSection>
                )}
              </div>
            )}
          </TabsContent>

          {/* Payment Methods Tab */}
          <TabsContent value="payment-methods">
            <div className="max-w-xl mx-auto">
              <p className="text-sm text-gray-400 mb-5">Configura cómo recibirás los pagos de tus actividades, además del tiempo límite para recibir reservas y confirmar pagos.</p>
              <div className="space-y-4">

              {/* Section 1: Métodos de pago */}
              <PaymentCard title="Métodos para recibir pagos" subtitle="Se aplicarán a todas tus actividades con costo." defaultOpen={false} testId="accordion-payment-methods">
                {/* Payment Link */}
                <div>
                  <Label htmlFor="pm-payment-link">Link de pago (PayPal, Mercado Pago, etc.)</Label>
                  <Input
                    id="pm-payment-link"
                    value={paymentMethods.payment_link}
                    onChange={(e) => setPaymentMethods(prev => ({ ...prev, payment_link: e.target.value }))}
                    placeholder="https://paypal.me/tu-usuario"
                    className="mt-1"
                    data-testid="pm-payment-link"
                  />
                </div>

                {/* Bank Info */}
                <div className="space-y-4 p-4 bg-gray-50 rounded-xl">
                  <div>
                    <Label className="text-sm font-medium">Datos bancarios para transferencia</Label>
                    <p className="text-xs text-gray-400 mt-0.5">Para que los usuarios puedan depositar o transferir</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pm-bank" className="text-xs">Banco</Label>
                      <Input
                        id="pm-bank"
                        value={paymentMethods.bank_info?.bank_name || ''}
                        onChange={(e) => setPaymentMethods(prev => ({ 
                          ...prev, 
                          bank_info: { ...prev.bank_info, bank_name: e.target.value } 
                        }))}
                        placeholder="Se detecta con la CLABE"
                        className="mt-1"
                        readOnly={!!getBankFromClabe(paymentMethods.bank_info?.clabe)}
                        data-testid="pm-bank-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pm-holder" className="text-xs">Titular</Label>
                      <Input
                        id="pm-holder"
                        value={paymentMethods.bank_info?.account_holder || ''}
                        onChange={(e) => setPaymentMethods(prev => ({ 
                          ...prev, 
                          bank_info: { ...prev.bank_info, account_holder: e.target.value } 
                        }))}
                        placeholder="Nombre completo"
                        className="mt-1"
                        data-testid="pm-account-holder"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="pm-clabe" className="text-xs">CLABE interbancaria</Label>
                    <Input
                      id="pm-clabe"
                      value={paymentMethods.bank_info?.clabe || ''}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 18);
                        const bank = getBankFromClabe(val);
                        setPaymentMethods(prev => ({ 
                          ...prev, 
                          bank_info: { ...prev.bank_info, clabe: val, ...(bank ? { bank_name: bank } : {}) } 
                        }));
                      }}
                      placeholder="18 dígitos"
                      inputMode="numeric"
                      maxLength={18}
                      className="mt-1 font-mono"
                      data-testid="pm-clabe"
                    />
                    {paymentMethods.bank_info?.clabe?.length >= 3 && (
                      <p className={`text-xs mt-1 ${getBankFromClabe(paymentMethods.bank_info.clabe) ? 'text-green-600' : 'text-gray-400'}`}>
                        {getBankFromClabe(paymentMethods.bank_info.clabe) ? `Banco detectado: ${getBankFromClabe(paymentMethods.bank_info.clabe)}` : 'Banco no identificado'}
                      </p>
                    )}
                  </div>
                </div>

                <Button
                  onClick={handleSavePaymentMethods}
                  disabled={savingPaymentMethods}
                  className="w-full bg-[#7f9df1] hover:bg-[#6b8be0] text-white"
                  data-testid="save-payment-methods-btn"
                >
                  {savingPaymentMethods ? 'Guardando...' : 'Guardar métodos de pago'}
                </Button>
              </PaymentCard>

              {/* Section 2: Ajustes de pagos */}
              <PaymentCard title="Ajustes de pagos" subtitle="Configura qué tan cerca del inicio de la actividad se aceptan reservas." defaultOpen={false} testId="accordion-payment-settings">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
                    <div className="flex-1 mr-3">
                      <p className="text-sm font-medium text-gray-700">48h - 24h antes</p>
                      <p className="text-xs text-gray-400">20 horas para pagar</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setPaymentRules(prev => ({ ...prev, range_48h_24h: !prev.range_48h_24h })); }}
                      className={`w-11 h-6 rounded-full transition-colors flex-shrink-0 ${paymentRules.range_48h_24h ? 'bg-green-500' : 'bg-gray-300'}`}
                      data-testid="toggle-48h-24h"
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${paymentRules.range_48h_24h ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-orange-50 rounded-xl">
                    <div className="flex-1 mr-3">
                      <p className="text-sm font-medium text-gray-700">24h - 6h antes</p>
                      <p className="text-xs text-gray-400">6 horas para pagar</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setPaymentRules(prev => ({ ...prev, range_24h_6h: !prev.range_24h_6h })); }}
                      className={`w-11 h-6 rounded-full transition-colors flex-shrink-0 ${paymentRules.range_24h_6h ? 'bg-green-500' : 'bg-gray-300'}`}
                      data-testid="toggle-24h-6h"
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${paymentRules.range_24h_6h ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
                    <div className="flex-1 mr-3">
                      <p className="text-sm font-medium text-gray-700">6h - 3h antes</p>
                      <p className="text-xs text-gray-400">3 horas para pagar</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setPaymentRules(prev => ({ ...prev, range_6h_3h: !prev.range_6h_3h })); }}
                      className={`w-11 h-6 rounded-full transition-colors flex-shrink-0 ${paymentRules.range_6h_3h ? 'bg-green-500' : 'bg-gray-300'}`}
                      data-testid="toggle-6h-3h"
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${paymentRules.range_6h_3h ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
                    </button>
                  </div>

                  <div className="p-3 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-500">Menos de <strong>3 horas</strong> antes del inicio → reservas cerradas automáticamente</p>
                  </div>
                </div>

                <Button
                  onClick={handleSavePaymentRules}
                  disabled={savingRules}
                  className="w-full bg-[#7f9df1] hover:bg-[#6b8be0] text-white"
                  data-testid="save-payment-settings-btn"
                >
                  {savingRules ? 'Guardando...' : 'Guardar ajustes de pagos'}
                </Button>
              </PaymentCard>

              {/* Section 3: Reglas de cancelación */}
              <PaymentCard title="Reglas de cancelación" subtitle="Define los porcentajes de reembolso cuando un usuario cancela. Se aplican automáticamente." defaultOpen={false} testId="accordion-cancellation-rules">
                <div className="space-y-3 mb-4">
                  <div>
                    <Label className="text-xs text-gray-500">Tiempo mínimo para cancelar (horas antes de la actividad)</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={cancellationRules.min_cancel_hours || ''}
                      onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); setCancellationRules(prev => ({ ...prev, min_cancel_hours: v ? parseInt(v) : '' })); }}
                      className="mt-1 w-32"
                      data-testid="min-cancel-hours"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">Cancelaciones con menos de {cancellationRules.min_cancel_hours || 0}h de anticipación serán bloqueadas (sin reembolso).</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Rangos de reembolso</Label>
                    {(cancellationRules.rules || []).map((rule, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-4">≥</span>
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={rule.min_hours}
                          onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); setCancellationRules(prev => { const r = [...prev.rules]; r[idx] = { ...r[idx], min_hours: v ? parseInt(v) : '' }; return { ...prev, rules: r }; }); }}
                          className="w-16 text-center text-xs"
                        />
                        <span className="text-xs text-gray-400">horas →</span>
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={rule.refund_percent}
                          onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); setCancellationRules(prev => { const r = [...prev.rules]; r[idx] = { ...r[idx], refund_percent: v ? Math.min(parseInt(v), 100) : '' }; return { ...prev, rules: r }; }); }}
                          className="w-16 text-center text-xs"
                        />
                        <span className="text-xs text-gray-400">% reembolso</span>
                        {(cancellationRules.rules || []).length > 1 && (
                          <button type="button" onClick={() => setCancellationRules(prev => ({ ...prev, rules: prev.rules.filter((_, i) => i !== idx) }))} className="text-red-400 hover:text-red-600">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setCancellationRules(prev => ({ ...prev, rules: [...(prev.rules || []), { min_hours: 12, refund_percent: 0 }] }))}
                      className="text-xs text-[#7f9df1] hover:underline"
                    >
                      + Agregar rango
                    </button>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-500 space-y-1">
                  <p className="font-medium">Resumen:</p>
                  {(cancellationRules.rules || []).sort((a, b) => b.min_hours - a.min_hours).map((r, i) => (
                    <p key={`summary-${r.min_hours}`}>• Más de {r.min_hours}h antes → {r.refund_percent}% de reembolso</p>
                  ))}
                  <p>• Menos de {cancellationRules.min_cancel_hours || 0}h antes → cancelación bloqueada</p>
                </div>

                <Button
                  onClick={handleSavePaymentRules}
                  disabled={savingRules}
                  className="w-full bg-[#7f9df1] hover:bg-[#6b8be0] text-white"
                  data-testid="save-cancellation-rules-btn"
                >
                  {savingRules ? 'Guardando...' : 'Guardar reglas de cancelación'}
                </Button>
              </PaymentCard>

              </div>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {!analytics ? (
              <p className="text-center text-gray-400 py-12">Cargando analytics...</p>
            ) : (
              <div className="space-y-6">
                {/* Filters */}
                <div className="grid grid-cols-2 gap-2">
                  <select value={analyticsFilter.status} onChange={(e) => handleAnalyticsFilterChange('status', e.target.value)} className="text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-2 bg-white w-full" data-testid="analytics-filter-status">
                    <option value="">Todas</option>
                    <option value="active">Solo activas</option>
                    <option value="past">Solo pasadas</option>
                  </select>
                  <select value={analyticsFilter.year} onChange={(e) => handleAnalyticsFilterChange('year', e.target.value)} className="text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-2 bg-white w-full" data-testid="analytics-filter-year">
                    <option value="">Todos los años</option>
                    {[2026, 2025, 2024].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <select value={analyticsFilter.month} onChange={(e) => handleAnalyticsFilterChange('month', e.target.value)} className="text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-2 bg-white w-full" disabled={!analyticsFilter.year} data-testid="analytics-filter-month">
                    <option value="">Todos los meses</option>
                    {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((m, i) => (
                      <option key={i+1} value={String(i+1)}>{m}</option>
                    ))}
                  </select>
                  <select value={analyticsFilter.activity_id} onChange={(e) => handleAnalyticsFilterChange('activity_id', e.target.value)} className="text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-2 bg-white w-full truncate" data-testid="analytics-filter-activity">
                    <option value="">Todas las actividades</option>
                    {(analytics?.all_activities || []).filter(a => a.is_active).map(a => (
                      <option key={a.activity_id} value={a.activity_id}>{a.title}</option>
                    ))}
                    {(analytics?.all_activities || []).some(a => !a.is_active) && (
                      <option disabled>── Pasadas ──</option>
                    )}
                    {(analytics?.all_activities || []).filter(a => !a.is_active).map(a => (
                      <option key={a.activity_id} value={a.activity_id}>{a.title}</option>
                    ))}
                  </select>
                </div>

                {/* Overview cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-slate-900/10 p-4">
                    <p className="text-xs text-gray-400 mb-1">Vistas totales</p>
                    <p className="text-2xl font-bold text-gray-500">{analytics.total_views}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-900/10 p-4">
                    <p className="text-xs text-gray-400 mb-1">Registros totales</p>
                    <p className="text-2xl font-bold text-gray-500">{analytics.total_registrations}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-900/10 p-4">
                    <p className="text-xs text-gray-400 mb-1">Capacidad total</p>
                    <p className="text-2xl font-bold text-gray-500">{analytics.total_capacity}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-900/10 p-4">
                    <p className="text-xs text-gray-400 mb-1">Tasa de conversión</p>
                    <p className="text-2xl font-bold text-[#7f9df1]">{analytics.conversion_rate}%</p>
                  </div>
                </div>

                {/* Active activities analytics */}
                {(analytics.active_activities || []).length > 0 && (
                  <CollapsibleSection title="Activas" count={(analytics.active_activities || []).length}>
                    {(analytics.active_activities || []).map((a) => (
                      <AnalyticsRow key={a.activity_id} activity={a} language={language} />
                    ))}
                  </CollapsibleSection>
                )}

                {/* Past activities analytics */}
                {(analytics.past_activities || []).length > 0 && (
                  <CollapsibleSection title="Pasadas" count={(analytics.past_activities || []).length} defaultOpen={(analytics.active_activities || []).length === 0}>
                    {(analytics.past_activities || []).map((a) => (
                      <AnalyticsRow key={a.activity_id} activity={a} isPast language={language} />
                    ))}
                  </CollapsibleSection>
                )}

                {(analytics.active_activities || []).length === 0 && (analytics.past_activities || []).length === 0 && (
                  <p className="text-gray-400 text-sm text-center py-8">No hay actividades aún</p>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
        )}

        {/* Create/Edit Activity Dialog */}
        <Dialog open={showCreateActivity} onOpenChange={setShowCreateActivity}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingActivity ? 'Editar actividad' : t('dashboard.createActivity')}
              </DialogTitle>
            </DialogHeader>
            {(() => {
              const hasReservations = editingActivity && (editingActivity.registered_count > 0 || editingActivity.confirmed_count > 0);
              return (
            <div className="space-y-6 py-4">
              {hasReservations && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  Esta actividad tiene reservas. Los campos de fecha, horario, precio, ubicación y cupo no se pueden modificar.
                </div>
              )}
              {/* Cover Image */}
              <div>
                <Label>{t('form.coverImage')} *</Label>
                <p className="text-xs text-gray-400 mt-0.5">Solo imágenes limpias sin texto ni logos. Formato horizontal recomendado (1200x800px).</p>
                <div className="mt-2">
                  {activityForm.cover_image ? (
                    <div className="relative w-full h-48 rounded-xl overflow-hidden">
                      <img src={activityForm.cover_image} alt="Cover" className="w-full h-full object-cover" />
                      <button
                        onClick={() => setActivityForm(prev => ({ ...prev, cover_image: '' }))}
                        className="absolute top-2 right-2 p-1 rounded-full bg-black/50 text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#7f9df1] transition-colors">
                      <ImageIcon className="w-8 h-8 text-slate-400 mb-2" />
                      <span className="text-sm text-slate-500">Click para subir imagen</span>
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, false)} />
                    </label>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <Label htmlFor="title">{t('form.title')} *</Label>
                <Input
                  id="title"
                  value={activityForm.title}
                  onChange={(e) => { if (e.target.value.length <= 80) setActivityForm(prev => ({ ...prev, title: e.target.value })); }}
                  maxLength={80}
                  className="mt-1"
                  data-testid="activity-title-input"
                />
                <p className={`text-xs mt-1 text-right ${activityForm.title.length >= 80 ? 'text-amber-500' : 'text-gray-300'}`}>{activityForm.title.length}/80</p>
              </div>

              {/* Primary Category */}
              <div>
                <Label>Categoría principal *</Label>
                <p className="text-xs text-gray-400 mt-0.5">Se mostrará en el feed de inicio</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(CATEGORIES).map(([key, { icon: Icon, color }]) => {
                    const selected = activityForm.categories[0] === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setActivityForm(prev => {
                            const others = prev.categories.slice(1).filter(c => c !== key);
                            return { ...prev, categories: [key, ...others] };
                          });
                        }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                          selected 
                            ? 'text-white border-transparent' 
                            : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                        }`}
                        style={selected ? { backgroundColor: color } : {}}
                        data-testid={`category-primary-${key}`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {t(`category.${key}`)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Secondary Categories */}
              {activityForm.categories[0] && (
              <div>
                <Label>Categorías secundarias (opcional, máx. 2)</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(CATEGORIES)
                    .filter(([key]) => key !== activityForm.categories[0])
                    .map(([key, { icon: Icon, color }]) => {
                    const selected = activityForm.categories.includes(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setActivityForm(prev => {
                            const secondary = prev.categories.slice(1);
                            if (secondary.includes(key)) {
                              return { ...prev, categories: [prev.categories[0], ...secondary.filter(c => c !== key)] };
                            }
                            if (secondary.length < 2) {
                              return { ...prev, categories: [...prev.categories, key] };
                            }
                            return prev;
                          });
                        }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                          selected 
                            ? 'text-white border-transparent' 
                            : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                        }`}
                        style={selected ? { backgroundColor: color } : {}}
                        data-testid={`category-secondary-${key}`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {t(`category.${key}`)}
                      </button>
                    );
                  })}
                </div>
              </div>
              )}

              {/* Short Description */}
              <div>
                <Label htmlFor="shortDesc">{t('form.shortDesc')} *</Label>
                <Textarea
                  id="shortDesc"
                  value={activityForm.short_description}
                  onChange={(e) => { if (e.target.value.length <= 200) setActivityForm(prev => ({ ...prev, short_description: e.target.value })); }}
                  maxLength={200}
                  rows={2}
                  className="mt-1"
                  data-testid="activity-short-desc-input"
                />
                <p className={`text-xs mt-1 text-right ${activityForm.short_description.length >= 200 ? 'text-amber-500' : 'text-gray-300'}`}>{activityForm.short_description.length}/200</p>
              </div>

              {/* Long Description */}
              <div>
                <Label htmlFor="longDesc">{t('form.longDesc')} *</Label>
                <Textarea
                  id="longDesc"
                  value={activityForm.long_description}
                  onChange={(e) => { if (e.target.value.length <= 2000) setActivityForm(prev => ({ ...prev, long_description: e.target.value })); }}
                  maxLength={2000}
                  rows={5}
                  className="mt-1"
                  data-testid="activity-long-desc-input"
                />
                <p className={`text-xs mt-1 text-right ${activityForm.long_description.length >= 2000 ? 'text-amber-500' : 'text-gray-300'}`}>{activityForm.long_description.length}/2000</p>
              </div>

              {/* Dates */}
              <div className={hasReservations ? 'opacity-60 pointer-events-none' : ''}>
                <Label>{t('form.date')} *</Label>
                {hasReservations && <p className="text-xs text-amber-500 mt-0.5">No editable con reservas activas</p>}
                <div className="mt-2 flex flex-col md:flex-row gap-4 items-center md:items-start">
                  <div className="flex justify-center w-full md:w-auto">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      disabled={(date) => date < new Date()}
                      className="rounded-md border"
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    {activityForm.dates.filter(d => d.date).map((dateInfo, idx) => (
                      <div key={dateInfo.date + dateInfo.time} className="p-2.5 bg-slate-50 rounded-lg space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="flex-1 text-sm min-w-0">{formatDate(dateInfo.date, language === 'es' ? 'es-MX' : 'en-US')}</span>
                          <Input
                            type="text"
                            inputMode="numeric"
                            value={dateInfo.capacity || ''}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '');
                              updateDateTime(idx, 'capacity', val ? parseInt(val) : '');
                            }}
                            placeholder="Cupo"
                            className="w-20 text-center shrink-0"
                            data-testid={`date-capacity-${idx}`}
                          />
                          <Button variant="ghost" size="sm" onClick={() => removeDate(idx)} className="h-7 w-7 p-0 shrink-0">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <TimeSelect
                            value={dateInfo.time}
                            onChange={(val) => updateDateTime(idx, 'time', val)}
                            className="flex-1 min-w-0"
                          />
                          <span className="text-sm text-gray-400">-</span>
                          <TimeSelect
                            value={dateInfo.end_time}
                            onChange={(val) => updateDateTime(idx, 'end_time', val)}
                            className="flex-1 min-w-0"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Min Age & Location */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="minAge">{t('form.minAge')} *</Label>
                  <Input
                    id="minAge"
                    type="text"
                    inputMode="numeric"
                    value={activityForm.min_age || ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setActivityForm(prev => ({ ...prev, min_age: val ? parseInt(val) : null }));
                    }}
                    className="mt-1"
                  />
                </div>
                <div />
              </div>

              {/* Location - structured */}
              <div className={hasReservations ? 'opacity-60 pointer-events-none' : ''}>
                <Label className="mb-1">Ubicación *</Label>
                {hasReservations && <p className="text-xs text-amber-500 mt-0.5">No editable con reservas activas</p>}
                <div className="grid grid-cols-2 gap-3 mt-1">
                  <select
                    value={activityForm.state}
                    onChange={(e) => setActivityForm(prev => ({ ...prev, state: e.target.value, city: '' }))}
                    className="h-10 rounded-lg border border-input bg-background pl-3 pr-8 text-sm select-styled"
                    data-testid="activity-state-select"
                  >
                    <option value="">Estado</option>
                    {getStates('México').map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {(() => {
                    const cities = getCities('México', activityForm.state);
                    const cityInList = cities.includes(activityForm.city);
                    const showCustom = activityForm.city === '__other__' || (activityForm.city && !cityInList);
                    return (
                      <select
                        value={cityInList ? activityForm.city : (activityForm.city ? '__other__' : '')}
                        onChange={(e) => setActivityForm(prev => ({ ...prev, city: e.target.value }))}
                        className="h-10 rounded-lg border border-input bg-background pl-3 pr-8 text-sm select-styled"
                        disabled={!activityForm.state}
                        data-testid="activity-city-select"
                      >
                        <option value="">Ciudad</option>
                        {cities.map(c => <option key={c} value={c}>{c}</option>)}
                        <option value="__other__">Otra ciudad...</option>
                      </select>
                    );
                  })()}
                </div>
                {activityForm.state && activityForm.city === '__other__' && (
                  <Input
                    value=""
                    onChange={(e) => setActivityForm(prev => ({ ...prev, city: e.target.value || '__other__' }))}
                    placeholder="Escribe la ciudad"
                    className="mt-2"
                    autoFocus
                    data-testid="activity-city-custom"
                  />
                )}
                {activityForm.state && activityForm.city && !getCities('México', activityForm.state).includes(activityForm.city) && activityForm.city !== '__other__' && (
                  <Input
                    value={activityForm.city}
                    onChange={(e) => setActivityForm(prev => ({ ...prev, city: e.target.value || '__other__' }))}
                    placeholder="Escribe la ciudad"
                    className="mt-2"
                    data-testid="activity-city-custom"
                  />
                )}
                <Input
                  value={activityForm.neighborhood}
                  onChange={(e) => setActivityForm(prev => ({ ...prev, neighborhood: e.target.value }))}
                  placeholder="Colonia"
                  className="mt-2"
                  data-testid="activity-neighborhood-input"
                />
                <Input
                  value={activityForm.address}
                  onChange={(e) => setActivityForm(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Calle y número"
                  className="mt-2"
                  data-testid="activity-address-input"
                />
              </div>

              {/* Price */}
              <div className={hasReservations ? 'opacity-60 pointer-events-none' : ''}>
                <Label htmlFor="price">{t('form.price')} (MXN) *</Label>
                {hasReservations && <p className="text-xs text-amber-500 mt-0.5">No editable con reservas activas</p>}
                <Input
                  id="price"
                  type="text"
                  inputMode="numeric"
                  value={activityForm.price}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setActivityForm(prev => ({ ...prev, price: val ? parseInt(val) : '' }));
                  }}
                  placeholder="0 = Gratis"
                  className="mt-1"
                  data-testid="activity-price-input"
                />
                <p className="text-xs text-gray-400 mt-1">0 para actividades gratuitas</p>
              </div>

              {/* Cancellation Policy - auto from rules + optional notes */}
              {parseFloat(activityForm.price) > 0 && (
                <div>
                  <Label>Política de cancelación *</Label>
                  
                  {/* Auto-generated rules from creator config */}
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs text-gray-600 space-y-1">
                    <p className="font-medium text-gray-700">Reglas aplicadas automáticamente:</p>
                    {(cancellationRules.rules || []).sort((a, b) => b.min_hours - a.min_hours).map((r, i) => (
                      <p key={`policy-${r.min_hours}`}>• Más de {r.min_hours}h antes → {r.refund_percent}% de reembolso</p>
                    ))}
                    <p>• Menos de {cancellationRules.min_cancel_hours || 24}h antes → Sin reembolso</p>
                    <p className="text-[10px] text-gray-400 mt-1">Estas reglas se configuran en Pagos → Reglas de cancelación</p>
                  </div>

                  {/* Optional notes */}
                  <Label className="mt-3 text-xs text-gray-400">Notas adicionales (opcional)</Label>
                  <Textarea
                    id="cancellationPolicy"
                    value={activityForm.cancellation_policy}
                    onChange={(e) => setActivityForm(prev => ({ ...prev, cancellation_policy: e.target.value }))}
                    rows={2}
                    className="mt-1"
                    placeholder="Ej: En caso de lluvia se reprogramará sin costo."
                    data-testid="activity-cancellation-policy"
                  />
                </div>
              )}

              {/* Allow Waitlist */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <Label className="text-sm font-medium">Permitir lista de espera</Label>
                  <p className="text-xs text-gray-400 mt-0.5">Los usuarios podrán anotarse cuando no haya cupo</p>
                </div>
                <Switch
                  checked={activityForm.allow_waitlist}
                  onCheckedChange={(checked) => setActivityForm(prev => ({ ...prev, allow_waitlist: checked }))}
                  data-testid="activity-waitlist-switch"
                />
              </div>

              {/* Coupon */}
              {parseFloat(activityForm.price) > 0 && (
              <div className="p-3 bg-gray-50 rounded-xl space-y-3 overflow-hidden">
                <div>
                  <Label className="text-sm font-medium">Cupón de descuento</Label>
                  <p className="text-xs text-gray-400 mt-0.5">Opcional. Crea un código de descuento exclusivo para esta actividad</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Código</Label>
                  <Input
                    value={activityForm.coupon.code}
                    onChange={(e) => setActivityForm(prev => ({ ...prev, coupon: { ...prev.coupon, code: e.target.value.toUpperCase().replace(/\s/g, '') } }))}
                    placeholder="EJ: PROMO50"
                    className="mt-1 text-xs uppercase"
                    maxLength={20}
                    data-testid="coupon-code-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-400">Descuento</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={activityForm.coupon.discount_amount}
                      onChange={(e) => { const val = e.target.value.replace(/\D/g, ''); setActivityForm(prev => ({ ...prev, coupon: { ...prev.coupon, discount_amount: val ? parseInt(val) : '' } })); }}
                      placeholder="$50"
                      className="mt-1 text-xs"
                      data-testid="coupon-amount-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">Usos máx.</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={activityForm.coupon.max_uses}
                      onChange={(e) => { const val = e.target.value.replace(/\D/g, ''); setActivityForm(prev => ({ ...prev, coupon: { ...prev.coupon, max_uses: val ? parseInt(val) : '' } })); }}
                      placeholder="100"
                      className="mt-1 text-xs"
                      data-testid="coupon-max-uses-input"
                    />
                  </div>
                </div>
                <div className="min-w-0 overflow-hidden">
                  <Label className="text-xs text-gray-400">Vigencia</Label>
                  <div className="mt-1 overflow-hidden">
                    <input
                      type="date"
                      value={activityForm.coupon.expires_at || ''}
                      onChange={(e) => setActivityForm(prev => ({ ...prev, coupon: { ...prev.coupon, expires_at: e.target.value } }))}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-xs shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      style={{ maxWidth: '100%', width: '100%', boxSizing: 'border-box' }}
                      data-testid="coupon-expires-input"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-gray-400">
                  {activityForm.coupon.expires_at 
                    ? <>Válido hasta el {new Date(activityForm.coupon.expires_at + 'T00:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}. <button type="button" onClick={() => setActivityForm(prev => ({ ...prev, coupon: { ...prev.coupon, expires_at: '' } }))} className="underline">Quitar fecha</button></>
                    : 'Sin fecha = expira con la actividad'
                  }
                </p>
                {activityForm.coupon.code && activityForm.coupon.discount_amount && (
                  <p className="text-xs text-green-600">
                    Cupón <strong>{activityForm.coupon.code}</strong>: -${activityForm.coupon.discount_amount} MXN por reserva
                  </p>
                )}
              </div>
              )}

              {/* FAQs */}
              <div>
                <Label>Preguntas frecuentes ({activityForm.faqs.length} agregadas) *</Label>
                <div className="mt-2 space-y-2">
                  {activityForm.faqs.map((faq, idx) => (
                    <div key={faq.question || `faq-${idx}`} className="p-3 bg-gray-50 rounded-lg relative">
                      <button
                        type="button"
                        onClick={() => setActivityForm(prev => ({
                          ...prev,
                          faqs: prev.faqs.filter((_, i) => i !== idx)
                        }))}
                        className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-200"
                      >
                        <X className="w-3 h-3 text-gray-400" />
                      </button>
                      <p className="text-sm font-medium pr-6">{faq.question}</p>
                      <p className="text-xs text-gray-500 mt-1">{faq.answer}</p>
                    </div>
                  ))}
                  <div className="space-y-2 mt-2">
                    <Input
                      placeholder="Pregunta..."
                      id="faq-question"
                      className="text-sm"
                      data-testid="faq-question-input"
                    />
                    <Textarea
                      placeholder="Respuesta..."
                      id="faq-answer"
                      rows={2}
                      className="text-sm"
                      data-testid="faq-answer-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const q = document.getElementById('faq-question').value.trim();
                        const a = document.getElementById('faq-answer').value.trim();
                        if (q && a) {
                          setActivityForm(prev => ({
                            ...prev,
                            faqs: [...prev.faqs, { question: q, answer: a }]
                          }));
                          document.getElementById('faq-question').value = '';
                          document.getElementById('faq-answer').value = '';
                        }
                      }}
                      data-testid="faq-add-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Agregar pregunta
                    </Button>
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div>
                <Label>Tags / Palabras clave ({activityForm.tags.length}/8)</Label>
                <p className="text-xs text-gray-400 mt-1 mb-2">Agrega palabras que ayuden a encontrar tu actividad (ej: yoga, relajación, arte)</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {activityForm.tags.map((tag, idx) => (
                    <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
                      #{tag}
                      <button
                        onClick={() => setActivityForm(prev => ({
                          ...prev,
                          tags: prev.tags.filter((_, i) => i !== idx)
                        }))}
                        className="text-gray-400 hover:text-red-400"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    id="tag-input"
                    placeholder="Escribe un tag"
                    className="flex-1"
                    disabled={activityForm.tags.length >= 8}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const val = e.target.value.trim().toLowerCase().replace(/^#/, '');
                        if (val && !activityForm.tags.includes(val) && activityForm.tags.length < 8) {
                          setActivityForm(prev => ({ ...prev, tags: [...prev.tags, val] }));
                          e.target.value = '';
                        }
                      }
                    }}
                    data-testid="tag-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={activityForm.tags.length >= 8}
                    onClick={() => {
                      const input = document.getElementById('tag-input');
                      const val = input.value.trim().toLowerCase().replace(/^#/, '');
                      if (val && !activityForm.tags.includes(val) && activityForm.tags.length < 8) {
                        setActivityForm(prev => ({ ...prev, tags: [...prev.tags, val] }));
                        input.value = '';
                      }
                    }}
                    data-testid="tag-add-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Agregar
                  </Button>
                </div>
                {activityForm.tags.length >= 8 && (
                  <p className="text-xs text-amber-500 mt-1">Máximo 8 tags alcanzado</p>
                )}
              </div>

              {/* Gallery */}
              <div>
                <Label>{t('form.gallery')} (mínimo 4, máx. 10) *</Label>
                <p className="text-xs text-gray-400 mt-1 mb-2">Solo imágenes limpias sin texto ni logos. Formato horizontal recomendado (1200x800px).</p>
                <div className="mt-2 grid grid-cols-5 gap-2">
                  {activityForm.gallery.map((img, idx) => (
                    <div key={img || idx} className="relative aspect-square rounded-lg overflow-hidden">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeGalleryImage(idx)}
                        className="absolute top-1 right-1 p-0.5 rounded-full bg-black/50 text-white"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {activityForm.gallery.length < 10 && (
                    <label className="aspect-square border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#7f9df1] transition-colors flex items-center justify-center">
                      <Plus className="w-6 h-6 text-slate-400" />
                      <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleImageUpload(e, true)} />
                    </label>
                  )}
                </div>
                {activityForm.gallery.length < 4 && (
                  <p className="text-xs text-amber-500 mt-2">Faltan {4 - activityForm.gallery.length} imagen(es) para completar el mínimo requerido</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => { setShowCreateActivity(false); resetActivityForm(); }}>
                  {t('form.cancel')}
                </Button>
                <Button 
                  onClick={handleSaveActivity}
                  disabled={saving || uploading}
                  className="bg-[#7f9df1] hover:bg-[#6b8be0]"
                  data-testid="save-activity-btn"
                >
                  {saving ? t('msg.loading') : t('form.save')}
                </Button>
              </div>
            </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Create Coupon Dialog */}
        <Dialog open={showCreateCoupon} onOpenChange={setShowCreateCoupon}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Crear cupón</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Código</Label>
                <Input
                  value={couponForm.code}
                  onChange={(e) => setCouponForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="DESCUENTO10"
                  className="mt-1"
                  data-testid="coupon-code-input"
                />
              </div>
              <div>
                <Label>Descuento (%)</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={couponForm.discount_percent}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setCouponForm(prev => ({ ...prev, discount_percent: val ? parseInt(val) : 0 }));
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Actividad</Label>
                <Select
                  value={couponForm.activity_id}
                  onValueChange={(value) => setCouponForm(prev => ({ ...prev, activity_id: value }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecciona una actividad" />
                  </SelectTrigger>
                  <SelectContent>
                    {dashboard?.active_activities?.map((activity) => (
                      <SelectItem key={activity.activity_id} value={activity.activity_id}>
                        {activity.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Máximo de usos</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={couponForm.max_uses}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setCouponForm(prev => ({ ...prev, max_uses: val ? parseInt(val) : 100 }));
                  }}
                  className="mt-1"
                />
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <Button variant="outline" onClick={() => setShowCreateCoupon(false)}>
                  {t('form.cancel')}
                </Button>
                <Button 
                  onClick={handleCreateCoupon}
                  className="bg-[#7f9df1] hover:bg-[#6b8be0]"
                  data-testid="save-coupon-btn"
                >
                  Crear
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Registrations Dialog */}
        <Dialog open={showRegistrations} onOpenChange={setShowRegistrations}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Registros - {selectedActivity?.title}</DialogTitle>
            </DialogHeader>
            <Tabs defaultValue="confirmed" className="w-full">
              <TabsList className="w-full">
                <TabsTrigger value="confirmed" className="flex-1">
                  Confirmados ({registrations.total_confirmed})
                </TabsTrigger>
                <TabsTrigger value="pending" className="flex-1">
                  Pendientes ({registrations.total_pending})
                </TabsTrigger>
                <TabsTrigger value="waitlist" className="flex-1">
                  Espera ({registrations.total_waitlist})
                </TabsTrigger>
              </TabsList>

              {/* Confirmed registrations */}
              <TabsContent value="confirmed" className="max-h-96 overflow-y-auto">
                {registrations.confirmed.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No hay registros confirmados</p>
                ) : (
                  <div className="space-y-2">
                    {registrations.confirmed.map((reg) => (
                      <div key={reg.registration_id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100" data-testid={`confirmed-reg-${reg.registration_id}`}>
                        <div>
                          <p className="font-medium">{reg.user_name}</p>
                          {reg.quantity > 1 && <p className="text-sm text-slate-500">{reg.quantity} lugares</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                            <CheckCircle className="w-3 h-3" /> Confirmado
                          </span>
                          <span className="text-xs text-slate-400">
                            {new Date(reg.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Pending payment registrations */}
              <TabsContent value="pending" className="max-h-96 overflow-y-auto">
                {/* Creator deadline warning */}
                {registrations.pending_payment.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                    <p className="text-xs text-amber-700">
                      Tienes <strong>24 horas</strong> para confirmar cada pago desde que el usuario realiza su reserva. Pasadas las 24h, se activa un periodo de gracia de <strong>24 horas adicionales</strong>. Si no confirmas en un total de 48 horas, la reserva se cancelará automáticamente y serás responsable de reembolsar cualquier pago ya recibido.
                    </p>
                  </div>
                )}
                {registrations.pending_payment.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No hay pagos pendientes</p>
                ) : (
                  <div className="space-y-2">
                    {registrations.pending_payment.map((reg) => {
                      const isOverdue = reg.status === 'payment_overdue';
                      return (
                      <div key={reg.registration_id} className={`flex items-center justify-between p-3 rounded-lg border ${isOverdue ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-100'}`} data-testid={`pending-reg-${reg.registration_id}`}>
                        <div>
                          <p className="font-medium">{reg.user_name}</p>
                          <p className={`text-xs mt-1 ${isOverdue ? 'text-red-600' : 'text-amber-600'}`}>
                            <Clock className="w-3 h-3 inline mr-1" />
                            {isOverdue ? 'Plazo vencido — 24h de gracia antes de cancelación' : 'Pendiente de pago · 24h para confirmar'}
                          </p>
                          {isOverdue && (
                            <p className="text-xs text-red-500 mt-0.5">Si ya recibiste el pago, confirma ahora. Si no confirmas en las próximas 24h, la reserva se cancelará y deberás reembolsar al usuario si ya pagó.</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1.5">
                          <Button
                            size="sm"
                            onClick={() => handleConfirmPayment(reg.registration_id)}
                            className="bg-[#7f9df1] hover:bg-[#6b8be0] text-white text-xs"
                            data-testid={`confirm-payment-btn-${reg.registration_id}`}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Confirmar pago
                          </Button>
                          {reg.status !== 'payment_not_found' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePaymentNotFound(reg.registration_id)}
                              className="text-xs text-red-500 border-red-200 hover:bg-red-50"
                              data-testid={`payment-not-found-btn-${reg.registration_id}`}
                            >
                              Pago no encontrado
                            </Button>
                          )}
                          {reg.status === 'payment_not_found' && (
                            <span className="text-xs text-red-500">Notificado (24h)</span>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* Waitlist */}
              <TabsContent value="waitlist" className="max-h-96 overflow-y-auto">
                {registrations.waitlist.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Lista de espera vacía</p>
                ) : (
                  <div className="space-y-2">
                    {registrations.waitlist.map((reg) => (
                      <div key={reg.registration_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`waitlist-reg-${reg.registration_id}`}>
                        <div>
                          <p className="font-medium">{reg.user_name}</p>
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(reg.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
        </div>
      </div>
    </div>
  );
};


// Payment Card - collapsible on mobile, always open on desktop
const PaymentCard = ({ title, subtitle, defaultOpen = false, children, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border border-slate-900/10 overflow-hidden" data-testid={testId}>
      <div
        onClick={() => setOpen(prev => !prev)}
        className="flex items-center justify-between p-5 cursor-pointer select-none"
      >
        <div className="min-w-0">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
        </div>
        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-3 ${open ? 'rotate-90' : ''}`} />
      </div>
      {open && (
        <div className="px-5 pb-5 space-y-5">
          {children}
        </div>
      )}
    </div>
  );
};

// Collapsible Section Component
const CollapsibleSection = ({ title, count, defaultOpen = true, badge, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between mb-3 group" data-testid={`collapse-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <div className="flex items-center gap-2">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{title} ({count})</h3>
          {badge}
        </div>
        <ChevronRight className={`w-4 h-4 text-gray-300 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="space-y-3">{children}</div>}
    </div>
  );
};

// Time remaining helper
const getTimeRemaining = (createdAt) => {
  if (!createdAt) return null;
  const created = new Date(createdAt);
  const deadline = new Date(created.getTime() + 48 * 60 * 60 * 1000);
  const now = new Date();
  const diff = deadline - now;
  if (diff <= 0) return 'Vencido';
  const hours = Math.ceil(diff / (1000 * 60 * 60));
  return `${hours}h restantes`;
};

const isOverdue = (createdAt) => {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  const deadline = new Date(created.getTime() + 48 * 60 * 60 * 1000);
  return new Date() > deadline;
};

const OverdueRefundCard = ({ reg, onConfirmPayment }) => {
  const [showRefund, setShowRefund] = useState(false);
  const [refundData, setRefundData] = useState(null);
  const [refundDone, setRefundDone] = useState(false);

  const loadRefundData = async () => {
    if (refundData) { setShowRefund(!showRefund); return; }
    try {
      const res = await usersApi.getProfile(reg.user_id);
      setRefundData(res.data);
      setShowRefund(true);
    } catch { setShowRefund(!showRefund); }
  };

  const handleMarkRefund = async () => {
    try {
      await registrationsApi.markRefundComplete(reg.registration_id);
      setRefundDone(true);
      toast.success('Reembolso marcado como completado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  if (refundDone) return (
    <div className="p-4 bg-green-50 rounded-xl border border-green-200">
      <p className="text-sm text-green-700 font-medium">{reg.user_name} — Reembolso completado</p>
    </div>
  );

  return (
    <div className="p-4 bg-red-50 rounded-xl border border-red-200" data-testid={`overdue-${reg.registration_id}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
          <div className="w-full h-full bg-red-100 flex items-center justify-center text-red-600 text-sm font-bold">
            {reg.user_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-700 truncate">{reg.user_name}</p>
          <p className="text-xs text-red-500 font-medium">
            {reg.status === 'cancelled_by_user' ? 'Cancelado por usuario' : 'Pago vencido'} — Reembolso requerido
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-bold text-red-600">${reg.refund_amount || reg.final_price || 0} MXN</p>
          {reg.refund_percent != null && reg.refund_percent < 100 && (
            <p className="text-[10px] text-red-400">{reg.refund_percent}% de ${reg.final_price || 0}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={loadRefundData}
          className="rounded-full text-xs h-8 px-3 text-red-600 border-red-200 hover:bg-red-100">
          <Eye className="w-3 h-3 mr-1" />{showRefund ? 'Ocultar datos' : 'Ver datos de reembolso'}
        </Button>
        <Button size="sm" onClick={() => onConfirmPayment(reg.registration_id)}
          className="bg-[#7f9df1] hover:bg-[#6b8be0] text-white rounded-full text-xs h-8 px-3">
          <CheckCircle className="w-3 h-3 mr-1" />Confirmar pago
        </Button>
        <Button size="sm" onClick={handleMarkRefund}
          className="bg-green-600 hover:bg-green-700 text-white rounded-full text-xs h-8 px-3">
          <CheckCircle className="w-3 h-3 mr-1" />Confirmar reembolso
        </Button>
      </div>
      {showRefund && (
        <div className="mt-3 p-3 bg-white rounded-lg text-xs space-y-1">
          {refundData?.refund_clabe && (
            <>
              <p><strong>CLABE:</strong> {refundData.refund_clabe}</p>
              {refundData.refund_clabe_holder && <p><strong>Titular:</strong> {refundData.refund_clabe_holder}</p>}
              {getBankFromClabe(refundData.refund_clabe) && <p className="text-green-600">Banco: {getBankFromClabe(refundData.refund_clabe)}</p>}
            </>
          )}
          {refundData?.refund_paypal && <p><strong>PayPal/MP:</strong> {refundData.refund_paypal}</p>}
          {!refundData?.refund_clabe && !refundData?.refund_paypal && (
            <p className="text-red-500">El usuario no ha configurado método de reembolso. Email: {reg.user_email}</p>
          )}
        </div>
      )}
    </div>
  );
};

// Activity Row Component
const ActivityRow = ({ activity, isPast, onOpen, onReuse, language }) => {
  const categoryInfo = CATEGORIES[activity.category];
  const CategoryIcon = categoryInfo?.icon;
  const hasNotification = !isPast && ((activity.pending_payments || 0) > 0 || (activity.proofs_pending || 0) > 0 || (activity.new_waitlist || 0) > 0);

  return (
    <button 
      onClick={onOpen}
      className={`w-full text-left bg-white rounded-xl p-4 border border-slate-900/10 flex items-center gap-4 transition-colors relative ${isPast ? 'opacity-60' : 'hover:border-[#7f9df1]/30'}`}
      data-testid={`activity-row-${activity.activity_id}`}
    >
      {/* Notification badge */}
      {hasNotification && (
        <div className="absolute top-3 right-3 md:top-4 md:right-4 w-3 h-3 bg-red-500 rounded-full border-2 border-white z-10" data-testid={`notif-badge-${activity.activity_id}`} />
      )}

      {/* Image */}
      <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden flex-shrink-0">
        <img 
          src={activity.cover_image || 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=200'} 
          alt={activity.title}
          className={`w-full h-full object-cover ${isPast ? 'grayscale' : ''}`}
        />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {CategoryIcon && (
            <span 
              className="p-1 rounded"
              style={{ backgroundColor: categoryInfo.bgColor, color: categoryInfo.textColor }}
            >
              <CategoryIcon className="w-3.5 h-3.5" />
            </span>
          )}
          <h3 className="font-semibold text-slate-900 text-sm truncate">{activity.title}</h3>
        </div>
        <p className="text-xs text-slate-500 mb-1">
          {formatDate(activity.dates[0]?.date, language === 'es' ? 'es-MX' : 'en-US')}
        </p>
        <span className="text-xs text-slate-400">
          {activity.confirmed_count ?? activity.registered_count}/{activity.capacity} confirmados
        </span>
      </div>

      {/* Arrow or Reuse */}
      <div className="flex-shrink-0">
        {isPast ? (
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onReuse?.(); }} data-testid={`reuse-activity-${activity.activity_id}`}>
            <Copy className="w-4 h-4 mr-1" />
            Reusar
          </Button>
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-300" />
        )}
      </div>
    </button>
  );
};

const AnalyticsRow = ({ activity: a, isPast, language }) => {
  const categoryInfo = CATEGORIES[a.category];
  const CategoryIcon = categoryInfo?.icon;
  return (
    <div className={`w-full bg-white rounded-xl p-4 border border-slate-900/10 ${isPast ? 'opacity-60' : ''}`} data-testid={`analytics-${a.activity_id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
          <img src={a.cover_image || 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=200'} alt={a.title} className={`w-full h-full object-cover ${isPast ? 'grayscale' : ''}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {CategoryIcon && <span className="p-1 rounded" style={{ backgroundColor: categoryInfo.bgColor, color: categoryInfo.textColor }}><CategoryIcon className="w-3 h-3" /></span>}
            <h3 className="font-semibold text-slate-900 text-sm truncate">{a.title}</h3>
          </div>
          <p className="text-xs text-slate-500">{formatDate(a.dates?.[0]?.date, language === 'es' ? 'es-MX' : 'en-US')}</p>
        </div>
      </div>
      <div className="grid grid-cols-5 gap-2 text-center">
        <div>
          <p className="text-[10px] text-gray-400">Vistas</p>
          <p className="text-sm font-bold text-gray-600">{a.views}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400">Registros</p>
          <p className="text-sm font-bold text-gray-600">{Math.max(0, a.registrations)}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400">Llenado</p>
          <p className="text-sm font-bold text-gray-600">{Math.max(0, a.fill_rate)}%</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400">Precio</p>
          <p className="text-sm font-bold text-gray-600">{a.price > 0 ? `$${a.price}` : 'Gratis'}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400">Ingresos</p>
          <p className="text-sm font-bold text-green-600">{a.revenue > 0 ? `$${a.revenue.toLocaleString('es-MX')}` : '$0'}</p>
        </div>
      </div>
      <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#7f9df1] rounded-full transition-all" style={{ width: `${Math.max(0, Math.min(a.fill_rate, 100))}%` }} />
      </div>
    </div>
  );
};


// Activity Detail View Component (Tasks 5 & 6)
const ActivityDetailView = ({ activity, registrations, onBack, onEdit, onDelete, onConfirmPayment, onPaymentNotFound, onActivityCancelled, language, token }) => {
  const categoryInfo = CATEGORIES[activity.category];
  const CategoryIcon = categoryInfo?.icon;
  const [showNewDateModal, setShowNewDateModal] = useState(false);
  const [tentativeDate, setTentativeDate] = useState('');
  const [tentativeTime, setTentativeTime] = useState('10:00');
  const [waitlistActionLoading, setWaitlistActionLoading] = useState(false);
  const [seenTabs, setSeenTabs] = useState(new Set());
  const [proofImage, setProofImage] = useState(null);

  const hasWaitlist = (registrations.waitlist?.length || 0) > 0;
  const hasPendingNotif = (activity.pending_payments || 0) > 0 && !seenTabs.has('pending');
  const hasWaitlistNotif = (activity.new_waitlist || 0) > 0 && !seenTabs.has('waitlist');

  const handleTabChange = (value) => {
    setSeenTabs(prev => new Set([...prev, value]));
  };

  const handleNewDate = async () => {
    if (!tentativeDate) { toast.error('Selecciona una fecha'); return; }
    setWaitlistActionLoading(true);
    try {
      const res = await activitiesApi.waitlistNewDate(activity.activity_id, {
        tentative_date: tentativeDate,
        tentative_time: tentativeTime,
        edit_before_publish: true
      });
      toast.success(`${res.data.notified_count} usuario(s) notificado(s)`);
      setShowNewDateModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al notificar');
    } finally {
      setWaitlistActionLoading(false);
    }
  };

  const handleNoNewDate = async () => {
    if (!window.confirm('¿Seguro? Se notificará a todos los de la lista de espera y se limpiará la lista.')) return;
    setWaitlistActionLoading(true);
    try {
      const res = await activitiesApi.waitlistNoNewDate(activity.activity_id);
      toast.success(`${res.data.notified_count} usuario(s) notificado(s). Lista limpiada.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setWaitlistActionLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="activity-detail-view">
      {/* Header with image */}
      <div className="bg-white rounded-xl border border-slate-900/10 overflow-hidden">
        <div className="relative h-40 md:h-52">
          <img 
            src={activity.cover_image || 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=600'} 
            alt={activity.title}
            className="w-full h-full object-cover"
          />
          {/* Back button */}
          <button
            onClick={onBack}
            className="absolute top-3 left-3 p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white"
            data-testid="activity-detail-back"
          >
            <ChevronLeft className="w-5 h-5 text-gray-500" />
          </button>
          {/* Edit + Delete */}
          <div className="absolute top-3 right-3 flex items-center gap-2">
            <button
              onClick={onEdit}
              className="p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white"
              data-testid="activity-detail-edit"
            >
              <Edit className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={onDelete}
              className="p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-red-50"
              data-testid="activity-detail-delete"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            {CategoryIcon && (
              <span className="p-1.5 rounded-lg" style={{ backgroundColor: categoryInfo.bgColor, color: categoryInfo.textColor }}>
                <CategoryIcon className="w-4 h-4" />
              </span>
            )}
            <h2 className="font-semibold text-lg text-slate-900">{activity.title}</h2>
          </div>
          <p className="text-sm text-slate-500">
            {formatDate(activity.dates[0]?.date, language === 'es' ? 'es-MX' : 'en-US')}
            {activity.dates[0]?.start_time && ` · ${activity.dates[0].start_time}`}
            {activity.dates[0]?.end_time && ` - ${activity.dates[0].end_time}`}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {activity.registered_count}/{activity.capacity} inscritos
            {activity.waitlist_count > 0 && ` · +${activity.waitlist_count} en espera`}
          </p>
        </div>
      </div>

      {/* Registration Tabs - hidden for cancelled activities */}
      {!activity.cancelled && (
      <Tabs defaultValue="confirmed" className="w-full" onValueChange={handleTabChange}>
        <TabsList className="w-full grid grid-cols-3 gap-1 bg-gray-100 p-1 rounded-xl h-auto">
          <TabsTrigger value="confirmed" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-[11px] py-2">
            Confirmados ({registrations.total_confirmed || registrations.confirmed?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="pending" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-[11px] py-2 relative">
            Pendientes ({registrations.total_pending || registrations.pending_payment?.length || 0})
            {hasPendingNotif && <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-gray-100" />}
          </TabsTrigger>
          <TabsTrigger value="waitlist" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-[11px] py-2 relative">
            Espera ({registrations.waitlist?.length || 0})
            {hasWaitlistNotif && <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-gray-100" />}
          </TabsTrigger>
        </TabsList>

        {/* Confirmados */}
        <TabsContent value="confirmed">
          {registrations.confirmed?.length === 0 ? (
            <div className="text-center py-8 bg-white rounded-xl border border-slate-900/10">
              <CheckCircle className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No hay registros confirmados</p>
            </div>
          ) : (() => {
            const active = registrations.confirmed.filter(r => r.status === 'confirmed');
            const cancelledRefund = registrations.confirmed.filter(r => r.status === 'cancelled_by_user' && r.refund_required);
            return (
              <div className="space-y-4">
                {cancelledRefund.length > 0 && (
                  <CollapsibleSection title="Reembolso pendiente" count={cancelledRefund.length}>
                    {cancelledRefund.map((reg) => (
                      <OverdueRefundCard key={reg.registration_id} reg={reg} onConfirmPayment={onConfirmPayment} />
                    ))}
                  </CollapsibleSection>
                )}
                {active.length > 0 && (
                  <CollapsibleSection title="Confirmados" count={active.length}>
                    {active.map((reg) => (
                      <div key={reg.registration_id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-900/10" data-testid={`detail-confirmed-${reg.registration_id}`}>
                        <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                          {reg.user_image ? (
                            <img src={reg.user_image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-green-100 flex items-center justify-center text-green-600 text-sm font-bold">
                              {reg.user_name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-700 truncate">{reg.user_name}</p>
                          <p className="text-xs text-gray-400">
                            {reg.quantity > 1 ? `${reg.quantity} lugares · ` : ''}
                            {new Date(reg.created_at).toLocaleDateString('es-MX')}
                          </p>
                        </div>
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2.5 py-1 rounded-full flex-shrink-0">
                          <CheckCircle className="w-3 h-3" /> Confirmado
                        </span>
                      </div>
                    ))}
                  </CollapsibleSection>
                )}
              </div>
            );
          })()}
        </TabsContent>

        {/* Pendientes */}
        <TabsContent value="pending">
          {registrations.pending_payment?.length === 0 ? (
            <div className="text-center py-8 bg-white rounded-xl border border-slate-900/10">
              <Clock className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">Sin pagos pendientes</p>
            </div>
          ) : (() => {
            const withProof = registrations.pending_payment.filter(r => r.payment_proof_sent && !isOverdue(r.created_at));
            const withoutProof = registrations.pending_payment.filter(r => !r.payment_proof_sent && !isOverdue(r.created_at));
            const overdue = registrations.pending_payment.filter(r => isOverdue(r.created_at));
            return (
              <div className="space-y-4">
                {/* Vencidos - Reembolso pendiente */}
                {overdue.length > 0 && (
                  <CollapsibleSection title="Vencidos - Reembolso pendiente" count={overdue.length}>
                    {overdue.map((reg) => (
                      <OverdueRefundCard key={reg.registration_id} reg={reg} onConfirmPayment={onConfirmPayment} />
                    ))}
                  </CollapsibleSection>
                )}

                {/* Pendiente confirmar pago (with proof) */}
                {withProof.length > 0 && (
                  <CollapsibleSection title="Pendiente confirmar pago" count={withProof.length}>
                    {withProof.map((reg) => (
                      <div key={reg.registration_id} className="p-4 bg-white rounded-xl border border-slate-900/10" data-testid={`detail-pending-${reg.registration_id}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                            {reg.user_image ? (
                              <img src={reg.user_image} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-amber-100 flex items-center justify-center text-amber-600 text-sm font-bold">
                                {reg.user_name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate">{reg.user_name}</p>
                            <p className="text-xs text-blue-500">Comprobante enviado{reg.quantity > 1 && ` · ${reg.quantity} lugares`}</p>
                          </div>
                          <span className="text-[10px] text-amber-500 font-medium flex-shrink-0">{getTimeRemaining(reg.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {reg.proof_image_url && (
                            <Button size="sm" variant="outline" onClick={() => setProofImage(reg.proof_image_url)}
                              className="rounded-full text-xs h-8 px-3 text-blue-600 border-blue-200 hover:bg-blue-50"
                              data-testid={`detail-viewproof-${reg.registration_id}`}>
                              <Eye className="w-3 h-3 mr-1" />Ver comprobante
                            </Button>
                          )}
                          <Button size="sm" onClick={() => onConfirmPayment(reg.registration_id)}
                            className="bg-[#7f9df1] hover:bg-[#6b8be0] text-white rounded-full text-xs h-8 px-3"
                            data-testid={`detail-confirm-${reg.registration_id}`}>
                            <CheckCircle className="w-3 h-3 mr-1" />Confirmar pago
                          </Button>
                          {reg.status !== 'payment_not_found' ? (
                            <Button size="sm" variant="outline" onClick={() => onPaymentNotFound(reg.registration_id)}
                              className="rounded-full text-xs h-8 px-3 text-red-500 border-red-200 hover:bg-red-50"
                              data-testid={`detail-notfound-${reg.registration_id}`}>
                              No encontrado
                            </Button>
                          ) : (
                            <span className="text-xs text-red-400">Notificado (24h)</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </CollapsibleSection>
                )}

                {/* Pendiente comprobante de pago (without proof) */}
                {withoutProof.length > 0 && (
                  <CollapsibleSection title="Pendiente comprobante de pago" count={withoutProof.length}>
                    <p className="text-xs text-gray-400 -mt-1 mb-2">Los usuarios tienen 48h para realizar su pago y subir comprobante.</p>
                    {withoutProof.map((reg) => (
                      <div key={reg.registration_id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-900/10" data-testid={`detail-nosproof-${reg.registration_id}`}>
                        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                          {reg.user_image ? (
                            <img src={reg.user_image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 text-sm font-bold">
                              {reg.user_name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-700 truncate">{reg.user_name}</p>
                          <p className="text-xs text-gray-400">{new Date(reg.created_at).toLocaleDateString('es-MX')}{reg.quantity > 1 && ` · ${reg.quantity} lugares`}</p>
                        </div>
                        <span className="text-[10px] text-amber-500 font-medium flex-shrink-0">{getTimeRemaining(reg.created_at)}</span>
                      </div>
                    ))}
                  </CollapsibleSection>
                )}
              </div>
            );
          })()}
        </TabsContent>

        {/* Espera */}
        <TabsContent value="waitlist">
          {registrations.waitlist?.length === 0 ? (
            <div className="text-center py-8 bg-white rounded-xl border border-slate-900/10">
              <Users className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">Lista de espera vacía</p>
            </div>
          ) : (
            <CollapsibleSection title="En espera" count={registrations.waitlist.length}>
              {registrations.waitlist.map((reg) => (
                <div key={reg.registration_id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-900/10" data-testid={`detail-waitlist-${reg.registration_id}`}>
                  <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                    {reg.user_image ? (
                      <img src={reg.user_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-bold">
                        {reg.user_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">{reg.user_name}</p>
                    <p className="text-xs text-gray-400">{new Date(reg.created_at).toLocaleDateString('es-MX')}</p>
                  </div>
                  <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full flex-shrink-0">Espera</span>
                </div>
              ))}

              {/* Waitlist actions */}
              <div className="pt-3 space-y-2" data-testid="waitlist-actions">
                <Button
                  onClick={() => setShowNewDateModal(true)}
                  className="w-full bg-primary hover:bg-primary-hover rounded-xl py-5 font-medium"
                  disabled={waitlistActionLoading}
                  data-testid="waitlist-new-date-btn"
                >
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Abrir nueva fecha
                </Button>
                <Button
                  variant="outline"
                  onClick={handleNoNewDate}
                  className="w-full rounded-xl py-5 text-gray-500 border-gray-200"
                  disabled={waitlistActionLoading}
                  data-testid="waitlist-no-date-btn"
                >
                  No habrá nueva fecha
                </Button>
              </div>
            </CollapsibleSection>
          )}
        </TabsContent>
      </Tabs>
      )}

      {/* New Date Modal */}
      {showNewDateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 space-y-4" data-testid="new-date-modal">
            <h3 className="text-lg font-semibold text-gray-700">Nueva fecha tentativa</h3>
            <p className="text-sm text-gray-400">
              Se notificará a {registrations.waitlist?.length || 0} persona(s) en lista de espera. 
              Tendrán 48 horas para confirmar su interés.
            </p>
            <div>
              <Label>Fecha</Label>
              <Input 
                type="date" 
                value={tentativeDate} 
                onChange={e => setTentativeDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="mt-1"
                data-testid="tentative-date-input"
              />
            </div>
            <div>
              <Label>Hora</Label>
              <Input 
                type="time" 
                value={tentativeTime} 
                onChange={e => setTentativeTime(e.target.value)}
                className="mt-1"
                data-testid="tentative-time-input"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleNewDate}
                className="flex-1 bg-primary hover:bg-primary-hover rounded-xl"
                disabled={waitlistActionLoading}
                data-testid="send-new-date-btn"
              >
                {waitlistActionLoading ? 'Enviando...' : 'Notificar lista de espera'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowNewDateModal(false)}
                className="rounded-xl"
                data-testid="cancel-new-date-btn"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Activity & Refunds */}
      {(registrations.confirmed?.length > 0 || registrations.pending_payment?.length > 0) && !activity.cancelled && (
        <div className="bg-white rounded-xl border border-red-200 p-4">
          <h3 className="text-sm font-semibold text-red-600 mb-2">Cancelar actividad</h3>
          <p className="text-xs text-gray-400 mb-3">Se notificará a todos los inscritos y se generará la lista de reembolsos.</p>
          <Button
            variant="outline"
            className="w-full border-red-200 text-red-600 hover:bg-red-50"
            onClick={async () => {
              if (!window.confirm('¿Estás seguro de cancelar esta actividad? Se notificará a todos los inscritos que su reembolso está en proceso.')) return;
              try {
                const res = await activitiesApi.cancelActivity(activity.activity_id);
                toast.success(`Actividad cancelada. ${res.data.affected_registrations} usuario(s) notificado(s).`);
                // Stay in detail view and show refund section
                onActivityCancelled({ ...activity, cancelled: true, is_active: false });
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al cancelar');
              }
            }}
            data-testid="cancel-activity-btn"
          >
            Cancelar actividad y notificar inscritos
          </Button>
        </div>
      )}

      {activity.cancelled && <RefundSection activityId={activity.activity_id} />}

      {/* Payment proof lightbox */}
      {proofImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setProofImage(null)}>
          <button onClick={() => setProofImage(null)} className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/40 transition-colors z-10" data-testid="close-proof-lightbox">
            <X className="w-6 h-6 text-white" />
          </button>
          <img src={proofImage} alt="Comprobante de pago" className="max-w-full max-h-full object-contain rounded-lg touch-pinch-zoom" style={{ touchAction: 'pinch-zoom' }} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};


const RefundSection = ({ activityId }) => {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    activitiesApi.getRefunds(activityId).then(res => {
      setRefunds(res.data.refunds || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [activityId]);

  const handleMarkComplete = async (registrationId) => {
    try {
      await registrationsApi.markRefundComplete(registrationId);
      setRefunds(prev => prev.map(r => r.registration_id === registrationId ? { ...r, refund_status: 'completed' } : r));
      toast.success('Reembolso marcado como completado. Usuario notificado.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  if (loading) return <p className="text-center text-gray-400 py-4 text-sm">Cargando reembolsos...</p>;
  if (refunds.length === 0) return null;

  const pending = refunds.filter(r => r.refund_status !== 'completed');
  const completed = refunds.filter(r => r.refund_status === 'completed');

  return (
    <div className="bg-white rounded-xl border border-amber-200 p-4 space-y-3" data-testid="refund-section">
      <h3 className="text-sm font-semibold text-amber-700">Reembolsos ({pending.length} pendientes / {completed.length} completados)</h3>
      {pending.length > 0 && (
        <div className="space-y-2">
          {pending.map(r => (
            <div key={r.registration_id} className="p-3 bg-amber-50 rounded-lg space-y-2" data-testid={`refund-${r.registration_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">{r.user_name}</p>
                  <p className="text-xs text-gray-400">{r.user_email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-amber-700">${(r.refund_amount || r.amount || 0)?.toLocaleString('es-MX')} MXN</p>
                  {r.refund_percent != null && r.refund_percent < 100 && (
                    <p className="text-[10px] text-amber-500">{r.refund_percent}% de ${r.amount?.toLocaleString('es-MX') || 0}</p>
                  )}
                </div>
              </div>
              {r.refund_clabe && (
                <div className="text-xs text-gray-500 bg-white rounded p-2">
                  <p><strong>CLABE:</strong> {r.refund_clabe}</p>
                  {r.refund_clabe_holder && <p><strong>Titular:</strong> {r.refund_clabe_holder}</p>}
                  {r.refund_clabe.length >= 3 && <p className="text-green-600">{getBankFromClabe(r.refund_clabe) || 'Banco no identificado'}</p>}
                </div>
              )}
              {r.refund_paypal && (
                <div className="text-xs text-gray-500 bg-white rounded p-2">
                  <p><strong>PayPal/MP:</strong> {r.refund_paypal}</p>
                </div>
              )}
              {!r.refund_clabe && !r.refund_paypal && (
                <p className="text-xs text-red-500">El usuario no ha configurado método de reembolso</p>
              )}
              <Button
                size="sm"
                className="w-full bg-green-600 hover:bg-green-700 text-white text-xs"
                onClick={() => handleMarkComplete(r.registration_id)}
                data-testid={`mark-refund-${r.registration_id}`}
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1" /> Marcar como reembolsado
              </Button>
            </div>
          ))}
        </div>
      )}
      {completed.length > 0 && (
        <CollapsibleSection title="Reembolsados" count={completed.length} defaultOpen={false}>
          {completed.map(r => (
            <div key={r.registration_id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-600">{r.user_name}</p>
                <p className="text-xs text-gray-400">{r.user_email}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-green-600">${r.amount?.toLocaleString('es-MX') || 0}</p>
                <p className="text-[10px] text-green-500">Completado</p>
              </div>
            </div>
          ))}
        </CollapsibleSection>
      )}
    </div>
  );
};


export default CreatorDashboard;